import type { StepDescriptor, StepId } from "./route-table.ts";
import type { SectionStateEntry } from "./types.ts";
import { resolveResumeTarget } from "./resume.ts";

/**
 * The step-order guard (AC-8, ADR-20260918-dd9e). Pure: given the id of
 * the step a customer has requested, the already-computed section states
 * (section-state.ts's single shared computation) and the step table, it
 * decides whether that step may render now.
 *
 * No import of the router, no import of any component, no I/O — the same
 * discipline resume.ts and section-state.ts already follow. This module
 * contains no step id and no step path literal of its own: every id it
 * reads comes from the `stepTable`/`sectionStates` arguments a caller
 * supplies, never from route-table.ts directly.
 *
 * The guard reuses resolveResumeTarget's own notion of "the earliest
 * unsettled step in declared order" rather than recomputing it, so there
 * is exactly one definition of that phrase in the feature (the same
 * single-shared-computation discipline ADR-20260918-dd9e requires of
 * section state).
 */
export interface StepGuardResult {
  /** True when the requested step may render as-is. */
  readonly allowed: boolean;
  /** Present exactly when `allowed` is false: the step the caller must
   * redirect to instead — the earliest step, in declared order, that is
   * not yet fully settled. The requested step must never render in this
   * case (AC-8). */
  readonly redirectTarget?: StepId;
}

/**
 * Evaluates the guard for `requestedStepId`. A step may render once every
 * step before it (in declared order) is fully settled — precisely the
 * condition resolveResumeTarget already encodes: a requested step at or
 * before the resolved resume target's order is allowed; a requested step
 * after it is blocked; a requested step is never itself trusted, so this
 * comparison is always made against the independently-recomputed target,
 * not against caller-supplied trust.
 *
 * Throws if `requestedStepId` or the resolved target step is not present
 * in `stepTable` — both are programmer errors (a step table missing one
 * of its own declared steps), never a customer-reachable condition.
 *
 * `isHostlessStepSettled` (OBJ-131/FIND-191) is forwarded verbatim to
 * `resolveResumeTarget` — the same settlement signal RenewalLanding.tsx
 * already feeds that function (OBJ-130/FIND-190) must also reach the
 * guard's own target resolution, or the two can disagree on the same
 * claim: the landing page reports a hostless step done while the guard,
 * still resolving against the default `() => false`, redirects a request
 * one step further back to that very (already-settled, now-blank) step.
 * Defaults to `() => false`, which reproduces the pre-fix behaviour
 * exactly for any caller that does not supply it.
 */
export function evaluateStepOrderGuard(
  requestedStepId: StepId,
  sectionStates: readonly SectionStateEntry[],
  stepTable: readonly StepDescriptor[],
  isHostlessStepSettled: (stepId: StepId) => boolean = () => false,
): StepGuardResult {
  const target = resolveResumeTarget(sectionStates, stepTable, isHostlessStepSettled);

  const requestedStep = stepTable.find((step) => step.id === requestedStepId);
  if (requestedStep === undefined) {
    throw new Error(`evaluateStepOrderGuard: requested step "${requestedStepId}" is not present in stepTable`);
  }

  const targetStep = stepTable.find((step) => step.id === target.targetStep);
  if (targetStep === undefined) {
    throw new Error(
      `evaluateStepOrderGuard: resolved target step "${target.targetStep}" is not present in stepTable`,
    );
  }

  // A step that hosts sections but has every one of those sections
  // reported "inapplicable" is not on the customer's current path at all
  // (AC-8, OBJ-62/FIND-53) — declared order alone cannot see this, because
  // order only distinguishes "earlier" from "later", not "on this path"
  // from "not on this path". A step with no hosted entries in
  // `sectionStates` (single-purpose steps, or a step whose sections simply
  // were not evaluated) is unaffected by this check and falls through to
  // the order comparison below.
  const requestedStepEntries = sectionStates.filter((entry) => entry.hostStepId === requestedStepId);
  if (requestedStepEntries.length > 0 && requestedStepEntries.every((entry) => entry.state === "inapplicable")) {
    return { allowed: false, redirectTarget: targetStep.id };
  }

  if (requestedStep.order <= targetStep.order) {
    return { allowed: true };
  }

  return { allowed: false, redirectTarget: targetStep.id };
}
